import {
  convertUTCToIST,
  getDateComparison,
  moneyFormat,
  pascalCase,
} from "utils";

export const API_MOCK_RES = {
  success: true,
  requestId: null,
  data: {
    pagination: {},
    tabCounts: {},
    orders: [],
  },
  timestamp: "",
};

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateSlash = (isoDate) => {
  if (!isoDate) {
    return "-";
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return `${pad2(date.getDate())}/${pad2(
    date.getMonth() + 1,
  )}/${date.getFullYear()}`;
};

const formatDatePretty = (isoDate) => {
  if (!isoDate) {
    return "-";
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const month = date.toLocaleString("en-IN", { month: "short" });
  return `${pad2(date.getDate())} ${month} ${date.getFullYear()}`;
};

const formatDateTime = (isoDateTime) => {
  if (!isoDateTime) {
    return "-";
  }

  const date = new Date(isoDateTime);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const time = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formatDateSlash(isoDateTime)} ${time}`;
};

const formatMt = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "0 MT";
  }

  // const parsed = Number(value);
  // const normalized = Number.isInteger(parsed)
  //   ? parsed.toFixed(0)
  //   : parsed.toFixed(2);
  return `${value} MT`;
};

const formatShippingAddress = (shipTo) => {
  if (!shipTo) return "-";
  const name = shipTo.name ? String(shipTo.name).trim() : "";
  const region = shipTo.region ? String(shipTo.region).trim() : "";
  const city = shipTo.city ? String(shipTo.city).trim() : "";

  const parts = [];
  if (name) parts.push(name);
  if (region) parts.push(parts.length ? "-" : "", region);
  if (city) parts.push(parts.length ? ", " : "", city);

  return parts.length ? pascalCase(parts.join("")) : "-";
};

const mapPaymentStatus = (status) => {
  if (status === "PARTIAL") {
    return "Partial";
  }

  if (status === "FULL") {
    return "Full";
  }

  return status || "-";
};

const getnotInvoicedQty = (item) => {
  const orderedQty = Number(item?.orderQtyMt || 0);
  const invoiced = Number(item?.invoicedQtyMt || 0);
  const shortCloseQty = Number(item?.shortClosedQty || 0);
  return formatMt(orderedQty - invoiced - shortCloseQty);
};

const arrToString = (arr) => {
  if (arr.length === 0) return "";
  return `[${arr.join(", ")}]`;
};

const mapLineItem = (item, order, doCreatedData = {}) => ({
  id: item?.materialId || "-",
  lineItemId: item?.lineItemId,
  sellerOrderId: item?.sellerOrderId,
  description: item?.materialDescription || "-",
  orderQty: formatMt(item?.orderQtyMt),
  fgReady: formatMt(item?.fgReadyQtyMt),
  expectedFg: formatMt(item?.expectedFgQtyMt),
  fgAwaited: formatMt(item?.fgAwaitedQtyMt),
  fgDueBy: formatDatePretty(item?.fgDueByDate),
  eligible: Number(item?.notInvoicedQty || 0) > 0,
  expectedFgBy: formatDatePretty(item?.fgDueByDate),
  invoicedQty: formatMt(item?.invoicedQtyMt),
  notInvoicedQty: getnotInvoicedQty(item),
  shortCloseQty: formatMt(item?.shortClosedQty),
  orderConfirmedAt: order?.orderConfirmedAt || null,
  hasPayment: ["PARTIAL", "FULL"].includes(order?.paymentStatus),
  doCreatedQtyMt: Number(item?.doCreatedQtyMt || 0),
  rateExclTax: moneyFormat(item?.rateExclTax) || "",
  sellerOrderRef: item?.sellerOrderRef || "-",
  sku: item?.sku || "",
  soNumbers: item?.soNumbers ? arrToString(item?.soNumbers) : "",
  sellerOlRefs: item?.sellerOlRefs ? arrToString(item?.sellerOlRefs) : "",
  materialShortDescription: item?.materialShortDescription || "",
  fgQty: formatMt((item?.fgReadyQtyMt || 0) + (item?.expectedFgQtyMt || 0)),
  orderQtyValue: item?.orderQtyMt || 0,
  fgReadyQtyValue: item?.fgReadyQtyMt || 0,
  expectedFgValue: item?.expectedFgQtyMt || 0,
  batchUpdates: item?.batchUpdates || [],
  ...doCreatedData,
});

const getOrderFgReadyTone = (fgReadyQty, totalQty) => {
  const ready = Number(fgReadyQty || 0);
  const total = Number(totalQty || 0);
  return ready === total ? "success" : ready > 0 ? "warning" : "danger";
};

const formattedDate = (date) => {
  const istDate = convertUTCToIST(date, "DD/MM/YYYY HH:mm");
  return istDate === "-" ? "-" : `${istDate} IST`;
};

const mapDoCreatedLineItems = (order) =>
  (order?.doDetails || []).filter(Boolean).flatMap((doDetail) =>
    (doDetail?.skuLineItems || []).filter(Boolean).map((lineItem) =>
      mapLineItem(lineItem, order, {
        doId: doDetail?.doId || "-",
        psId: doDetail?.psId || "-",
        doCreatedOn: doDetail?.doCreatedOn
          ? formattedDate(doDetail.doCreatedOn)
          : "-",
        doReleasedOn: doDetail?.doReleasedOn
          ? formattedDate(doDetail.doReleasedOn)
          : "-",
        doCancelledOn: doDetail?.doCancellationOn
          ? formattedDate(doDetail.doCancellationOn)
          : "-",
        transactionStatus: doDetail?.transactionStatus || "-",
        doCreatedBy: doDetail?.createdBy || "-",
        doLastModifiedBy: doDetail?.lastModifiedBy || "-",
      }),
    ),
  );

export const mapOrder = (order, selectedTab) => {
  const isDoCreatedTab = selectedTab === "doCreated";

  return {
    buyerOrderNo: order.buyerOrderNo || "-",
    orderId: order.orderId,
    orderType: order.orderType || "-",
    sellerName: order.sellerName || "-",
    sellerOrder: order.sellerOrderNo || "-",
    expectedDispatchDate: formatDateSlash(order.expectedDispatchDate),
    buyer: pascalCase(order.customerName) || "-",
    shipTo: formatShippingAddress(order.shipTo),
    totalQty: formatMt(order.totalQtyMt),
    fgReady: `${formatMt(order.fgReadyQtyMt).replace(" MT", "")} / ${formatMt(
      order.totalQtyMt,
    )}`,
    fgReadyTone: getOrderFgReadyTone(order.fgReadyQtyMt, order.totalQtyMt),
    payment: mapPaymentStatus(order.paymentStatus),
    fgReadyBy: formatDateSlash(order.fgReadyByDate),
    fgReadyByDelta: `${
      Number(order.fgDaysFromToday || 0) >= 0 ? "+" : ""
    }${Number(order.fgDaysFromToday || 0)} days`,
    ageing: `${Number(order.agingDays || 0)} days`,
    tabs: order.tabs || [],
    hasException: Boolean(order.hasException),
    timeline: [
      {
        label: "Order Placed at:",
        value: formatDateTime(order.orderPlacedAt),
      },
      {
        label: "Order Confirmed at:",
        value: formatDateTime(order.orderConfirmedAt),
      },
      {
        label: "Expected Dispatch by:",
        value: formatDateSlash(order.expectedDispatchDate),
      },
    ],
    sellerInfo: {
      sourceSeller: {
        name: pascalCase(order?.sellerDetails?.name) || "-",
        mobile: order.sellerDetails?.mobile || "-",
        email: order.sellerDetails?.email || "-",
        gstin: order.sellerDetails?.gstin || "-",
        shipFrom: order.sellerDetails?.shipFromAddress?.addressLine1 || "-",
        billFrom: order.sellerDetails?.billFromAddress?.addressLine1 || "-",
        sellerDisplayName: pascalCase(order.sellerDetails?.displayName) || "-",
      },
      jodl: {
        name: pascalCase(order.sellerDetails?.jodlDetails?.name) || "-",
        gstin: order.sellerDetails?.jodlDetails?.gstin || "-",
      },
    },
    lineItems: isDoCreatedTab
      ? mapDoCreatedLineItems(order)
      : (order?.skuLineItems || [])
          .filter(Boolean)
          .map((lineItem) => mapLineItem(lineItem, order)),
    deliveryType: order?.deliveryType || "-",
    fgReadyQtyMt: order?.fgReadyQtyMt || 0,
    totalQtyMt: order?.totalQtyMt || 0,
    tab: selectedTab,
    paymentRequest: order?.paymentRequest || 0,
    creditRequest: order?.creditRequest || 0,
    preDoReleaseIds: order?.preDoReleaseIds || "-",
    preDoId: order?.preDoId || "-",
    doId: order?.doId || "",
    doCreatedOn: order?.doCreatedOn ? formattedDate(order.doCreatedOn) : "-",
    transactionStatus: order?.transactionStatus || "-",
    sellerAddressMetadata: order?.sellerAddressMetadata,
    pickupSlotFrom: order?.pickupSlotFrom,
    pickupSlotTo: order?.pickupSlotTo,
    psId: order?.psId || "-",
  };
};

export const FG_TRACKING_ORDERS = {
  totalCount: API_MOCK_RES.data?.pagination?.totalRecords || 0,
  offset: API_MOCK_RES.data?.pagination?.currentPage || 1,
  data: (API_MOCK_RES.data?.orders || []).map(mapOrder),
};

export const getMaxToDate = (fromDate) => {
  if (!fromDate) return "";
  const maxDate = new Date(fromDate);
  maxDate.setMonth(maxDate.getMonth() + 3);
  const today = new Date();
  return maxDate > today ? today : maxDate;
};

const parseNumberFromString = (str) => {
  if (!str) return null;
  const m = str.match(/-?\d+/);
  if (!m) return null;
  const v = Number(m[0]);
  return Number.isNaN(v) ? null : v;
};

export const getFormattedAgeing = (ageing) => {
  const n = parseNumberFromString(ageing);
  if (n === null) return ageing || "-";
  const unit = Math.abs(n) <= 1 ? "day" : "days";
  return `${n} ${unit}`;
};

export const getDaysFromDelta = (deltaStr) => {
  if (!deltaStr) return null;
  const match = deltaStr.match(/-?\d+/);
  if (!match) return null;
  const val = Number(match[0]);
  return Number.isNaN(val) ? null : val;
};

export const formatDeltaLabel = (deltaStr) => {
  const days = getDaysFromDelta(deltaStr);
  if (days === null) return deltaStr || "-";
  const abs = Math.abs(days);
  const unit = abs <= 1 ? "day" : "days";
  const sign = days > 0 ? "+" : days < 0 ? "" : "+";
  // for 0, show +0
  const displayNumber = `${sign}${days}`;
  return `${displayNumber} ${unit}`;
};

export const getVmIdsBySellerNames = (sellerData = [], sellerNames = []) => {
  const data = sellerData || [];
  const names = sellerNames || [];

  if (!data.length || !names.length) return [];

  return data
    .filter(({ companyName }) => names.includes(companyName))
    .map(({ vmId }) => vmId)
    .filter(Boolean);
};

export const getVmIdsByStates = (sellerData = [], states = []) => {
  const data = sellerData || [];
  const stateList = states || [];

  if (!data.length || !stateList.length) return [];

  return data
    .filter(({ state }) => stateList.includes(state))
    .map(({ vmId }) => vmId);
};

export const getUniqueStatesFromSellerData = (sellerData) => {
  const data = sellerData ?? [];
  return [...new Set(data.map(({ state }) => state))].filter(Boolean);
};

export const getCompanyNamesFromSellerData = (sellerData) => {
  const data = sellerData ?? [];
  return [...new Set(data.map(({ companyName }) => companyName))].filter(
    Boolean,
  );
};

export const getFallbackFgTrackingData = () => ({
  pagination: API_MOCK_RES.data?.pagination || {},
  tabCounts: API_MOCK_RES.data?.tabCounts || {},
  orders: FG_TRACKING_ORDERS.data || [],
});

export const normalizeFgTrackingData = ({ data = {} } = {}, selectedTab) => ({
  pagination: data.pagination || {},
  tabCounts: data.tabCounts || {},
  orders: (Array.isArray(data.orders) ? data.orders : [])
    .filter(Boolean)
    .map((order) => mapOrder(order, selectedTab)),
});

export const isFgUpdated = (items) => {
  return items.some((item) => {
    const fgReady = Number.parseFloat(item.fgReady) || 0;
    const expectedFg = Number.parseFloat(item.expectedFg) || 0;

    return fgReady > 0 || expectedFg > 0;
  });
};

const validateValue = (value) => {
  return value ? `${value}, ` : "";
};

export const getAddress = (selectedAddress) => {
  if (!selectedAddress) return "-";
  const {
    address: { address_line_1, address_line_2, city, pincode, state },
  } = selectedAddress;
  const formattedAddress =
    validateValue(address_line_1) +
    validateValue(address_line_2) +
    validateValue(pincode) +
    validateValue(city) +
    validateValue(state);

  return formattedAddress ?? "-";
};

export const getFormattedPaymentRequest = (paymentRequestBreakup) => {
  return [
    {
      label: "Advance required",
      value: moneyFormat(paymentRequestBreakup?.advanceRequired),
    },
    {
      label: "Prorate advance blocked",
      value: moneyFormat(paymentRequestBreakup?.proRatedAdvanceBlocked),
    },
    {
      label: "Amount to be blocked",
      value: moneyFormat(paymentRequestBreakup?.amountToBeBlocked),
    },
  ];
};

export const getFormattedCreditRequest = (creditRequestBreakup) => {
  return [
    {
      label: "Credit required",
      value: moneyFormat(creditRequestBreakup?.creditRequired),
    },
    {
      label: "Amount to be blocked",
      value: moneyFormat(creditRequestBreakup?.amountToBeBlocked),
    },
    {
      label: "Usable limit",
      value: moneyFormat(creditRequestBreakup?.usableCreditLimit),
    },
  ];
};

export const formatTimelineDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatTimelineDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const timePart = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${formatTimelineDate(value)}, ${timePart}`;
};

export const getExpectedFgTotal = (expectedFgQty = []) =>
  expectedFgQty.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

export const parseMtValue = (value) => {
  if (!value || typeof value !== "string") {
    return 0;
  }

  const parsed = Number.parseFloat(value.replace(" MT", ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getQuantityBreakdownTooltip = (lineItem) => {
  const invoicedQty = lineItem.invoicedQty || "0 MT";
  const notInvoicedQty = lineItem.notInvoicedQty || "0 MT";
  const shortCloseQty = lineItem.shortCloseQty || "0 MT";

  return `Order Qty = Invoiced (${invoicedQty}) + Not invoiced (${notInvoicedQty}) + Shortclosed (${shortCloseQty})`;
};

export const getFgToneColor = (tone, theme) => {
  if (tone === "success") {
    return theme.palette.green.dark;
  }

  if (tone === "warning") {
    return theme.palette.warning.dark;
  }

  if (tone === "danger") {
    return theme.palette.error.dark;
  }

  return theme.palette.text.secondary;
};

const getDateBasedTone = (value) => {
  const comparison = getDateComparison(value);

  if (comparison === null) {
    return "neutral";
  }

  if (comparison > 0) {
    return "success";
  }

  if (comparison === 0) {
    return "warning";
  }

  return "danger";
};

const getFgNotUpdated = (fgReadyQty, fgExpectedQty, fgAwaitedQty) => {
  return fgReadyQty === 0 && fgExpectedQty === 0 && fgAwaitedQty > 0;
};

export const getExpectedFgTone = (lineItem) =>
  getDateBasedTone(lineItem.fgDueBy);

export const getFgDueByTone = (lineItem) => getDateBasedTone(lineItem.fgDueBy);

export const getFgAwaitedTone = (lineItem) => {
  if (!lineItem.orderConfirmedAt) {
    return "neutral";
  }

  const confirmationDate = new Date(lineItem.orderConfirmedAt);
  if (Number.isNaN(confirmationDate.getTime())) {
    return "neutral";
  }

  const hoursSinceConfirmation =
    (Date.now() - confirmationDate.getTime()) / (1000 * 60 * 60);

  if (hoursSinceConfirmation <= 4) {
    return "success";
  }

  if (hoursSinceConfirmation <= 24) {
    return "warning";
  }

  return "danger";
};

export const getLineItemFgReadyTone = (lineItem) => {
  const fgReadyQty = parseMtValue(lineItem.fgReady);
  const fgExpectedQty = parseMtValue(lineItem.expectedFg);
  const fgAwaitedQty = parseMtValue(lineItem.fgAwaited);
  const dueDateComparison = getDateComparison(lineItem.fgDueBy);
  const hasDoCreatedQty = Number(lineItem.doCreatedQtyMt || 0) > 0;
  const isFgNotUpdated = getFgNotUpdated(
    fgReadyQty,
    fgExpectedQty,
    fgAwaitedQty,
  );

  if ((dueDateComparison < 0 || hasDoCreatedQty) && isFgNotUpdated) {
    return "danger";
  }

  if (fgReadyQty > 0 && dueDateComparison >= 0) {
    return "success";
  }

  if (isFgNotUpdated && (dueDateComparison === 0 || lineItem.hasPayment)) {
    return "warning";
  }

  return "neutral";
};

export const getIsUpdateFgHidden = (lineItem) => {
  const fgReadyQty = parseMtValue(lineItem.fgReady);
  const fgExpectedQty = parseMtValue(lineItem.expectedFg);
  const fgAwaitedQty = parseMtValue(lineItem.fgAwaited);
  const orderQty = parseMtValue(lineItem.orderQty);
  const invoicedQty = parseMtValue(lineItem.invoicedQty);
  const shortCloseQty = parseMtValue(lineItem.shortCloseQty);

  return (
    (fgReadyQty === 0 && fgExpectedQty === 0 && fgAwaitedQty === 0) ||
    orderQty === invoicedQty + shortCloseQty
  );
};

export const getJoinedText = (items) => items.filter(Boolean).join(" · ");

export const getBulkConfirmationMessage = ({
  items,
  isExpectedFgSelected,
  dueDate,
  formatDialogDate,
}) => {
  if (!items.length) {
    return "";
  }

  const itemLabel = `line item${items.length > 1 ? "s" : ""}`;

  if (isExpectedFgSelected) {
    return `Will mark ${
      items.length
    } ${itemLabel} as Expected FG, due ${formatDialogDate(dueDate)}`;
  }

  return `Will mark ${items.length} ${itemLabel} as FG ready in full qty`;
};